import { difference, intersection, uniq } from 'lodash';
import moment from 'moment-timezone';
import { BulkImportStatusMap } from '/src/features/object-templates/manage/helpers/field-operator.helper.js';

const operatorMap = {
  formSaved: '',
  formCompleted: '',
  'IS CLICKED': '!= null',
  'IS CHECKED': '!= null',
  'IS NOT CHECKED': '== null',
  IS: '==',
  'IS NOT': '!=',
  'IS NULL': '== null',
  'IS NOT NULL': '!= null',
  'IS GREATER THAN': '>',
  'IS GREATER THAN OR EQ': '>=',
  'IS SMALLER THAN': '<',
  'IS SMALLER THAN OR EQ': '<=',
  'IS STEP COMPLETED': '>=',
  'IS STEP NOT COMPLETED': '<'
};

export const clusterCondition = (filter, fieldsMap, dynamicValues = {}) => {
  const operator = filter.operator || filter.condition === 'OR' ? '||' : '&&';
  if (filter.groups.length === 0 && filter.rules.length === 0) {
    return '(true)';
  }
  return (
    '(' +
    filter.groups.map((group) => clusterCondition(group, fieldsMap, dynamicValues)).join(' ' + operator + ' ') +
    (filter.groups.length && filter.rules && filter.rules.length ? ' ' + operator + ' ' : '') +
    filter.rules.map((rule) => '(' + computeRule(rule, fieldsMap, dynamicValues) + ')').join(' ' + operator + ' ') +
    ')'
  );
};

/**
 * Returns storage type for different field types
 * @param {*} objectField
 */
const getStorageType = (objectField) => {
  if (
    (['REFERENCE', 'USER'].includes(objectField.FieldType) && objectField.Settings.IsMultiSelect) ||
    objectField.FieldType === 'TAG'
  ) {
    return 'Array';
  } else if (['ENTITY', 'CHECKBOX'].includes(objectField.FieldType)) {
    return 'Object'; //For entity comma separated string is generated handled using advanceConditions
  } else if (objectField.FieldType === 'DROPDOWN' && objectField.Settings.IsMultiSelect) {
    return 'Array';
  }
  return 'String';
};

/**
 * Returns conditional statement for IS NULL & IS NOT NULL operators
 */
const nullChecks = (storageType, field, rule) => {
  const isNull = rule.operator === 'IS NULL';
  switch (storageType) {
    case 'Array':
      return isNull ? `(!${field} || ${field}.length === 0)` : `(${field} && ${field}.length !== 0)`;
    case 'Object':
      return `this.advanceConditions(${field}, null, "${rule.operator}")`;
    default:
      return isNull
        ? `${field} ${operatorMap[rule.operator]} || ${field} === ''`
        : `${field} ${operatorMap[rule.operator]} && ${field} !== ''`;
  }
};

/**
 * Returns conditional statement for IS & IS NOT operators
 */
const isChecks = (storageType, field, rule, objectField) => {
  switch (storageType) {
    case 'Object':
      return `this.advanceConditions(${field}, ${_surroundWithQuotes(rule.value, rule.isDynamicValue)}, "${
        rule.operator
      }", "${objectField?.FieldType}")`;
    case 'Array':
      return rule.operator === 'IS'
        ? `${field} && ${field}.length === 1 && (${field}[0] == ${_surroundWithQuotes(
            rule.value,
            rule.isDynamicValue
          )})`
        : `!${field} || ${field}.length !== 1 || (${field}[0] != ${_surroundWithQuotes(
            rule.value,
            rule.isDynamicValue
          )})`;
    default:
      if (objectField?.FieldType === 'DATE') {
        const hasTime = objectField?.Settings?.IsTimeInputEnable === 1;
        if (hasTime) {
          return `new Date(${field}).getTime() ${operatorMap[rule.operator]} new Date(${_surroundWithQuotes(
            rule.value,
            rule.isDynamicValue
          )}).getTime()`;
        } else {
          return `new Date(new Date(${field}).toDateString()).getTime() ${operatorMap[rule.operator]} new Date(new Date(${_surroundWithQuotes(
            rule.value,
            rule.isDynamicValue
          )}).toDateString()).getTime()`;
        }
      }
      return `${field} ${operatorMap[rule.operator]} ${_surroundWithQuotes(rule.value, rule.isDynamicValue)}`;
  }
};

// Compare values.
const compareValues = (field, rule, fieldType) => {
  if (fieldType === 'DATE') {
    return `(new Date(${field}).getTime() ${operatorMap[rule.operator]} new Date(${_surroundWithQuotes(
      rule.value,
      rule.isDynamicValue
    )}).getTime())`;
  }

  if (rule.isDynamicValue) {
    return `(${field} || 0) ${operatorMap[rule.operator]} ${rule.value}`;
  }

  return `(${field} || 0) ${operatorMap[rule.operator]} ${Number(rule.value)}`;
};

const compareBulkImportSteps = (field, rule) => {
  const bulkImportStep = field.match(/Data [a-z]*/i)[0];
  const conditionStatus = rule.value;
  return `${field.replace(/_Data [a-z]*/i, '')} ${
    conditionStatus === 'FAILED' ? '==' : operatorMap[rule.operator]
  } ${BulkImportStatusMap[bulkImportStep][conditionStatus].value}`;
};

// Prepare date range for BETWEEN / NOT BETWEEN.
const prepareDateRange = (field, rule) => {
  const timeMapping = {
    'Current Year': 'year',
    'Current Month': 'month',
    'Current Week': 'isoweek',
    'Current Day': 'day'
  };
  const startDate = moment().startOf(timeMapping[rule.value]).valueOf();

  const endDate = moment().endOf(timeMapping[rule.value]).valueOf();

  if (rule.operator === 'BETWEEN') {
    return `((new Date(${field}).getTime() >= ${startDate}) && (new Date(${field}).getTime() <= ${endDate}))`;
  }
  return `((new Date(${field}).getTime() < ${startDate}) || (new Date(${field}).getTime() > ${endDate}))`;
};

const escapeQuotesAndSlash = (value) => {
  if (value && typeof value === 'string') {
    value = value.replace(/\\/g, '\\\\');
    return value.replace(/'/g, "\\'");
  }
  return value;
};

export const checkForRoles = (loggedInUserGroups) => (field) => {
  const conditionEval = (f) => !!f?.toLowerCase().startsWith('r') && !!loggedInUserGroups?.includes(f?.toLowerCase());

  if (Array.isArray(field)) {
    return field.some((f) => conditionEval(f));
  }

  return conditionEval(field);
};

export const computeRule = (ruleToCompute, fieldsMap, dynamicValues) => {
  let rule = ruleToCompute;
  try {
    rule = JSON.parse(JSON.stringify(ruleToCompute));
  } catch (exception) {
    // eslint-disable-next-line
    console.error('error while parsing json rule', ruleToCompute);
  }
  let field;
  let storageType = 'String';
  const objectField = fieldsMap[rule.key];
  if (objectField) {
    field = `this.modifiedRecord['${objectField.DatabaseColumnName}']`;
    storageType = getStorageType(objectField);
  } else {
    field = `this.elementValues['${rule.key}']`;
  }
  const checkRoles = ['UserRoles', 'UserAndRoles'].includes(objectField?.Settings?.fieldOfReference);
  const isDynamicValue = rule.value && typeof rule.value === 'string' && rule.value.match(/\[\[.*\]\]/);
  if (isDynamicValue) {
    rule.value = rule.value.replace(/\[\[([\s\S]+?)\]\]/g, (_match, columnName) => {
      if (columnName.trim().toLowerCase() === 'new date()') {
        return 'new Date()';
      }
      const field = Object.values(fieldsMap).find(
        (field) => field.DatabaseColumnName.toLowerCase() === columnName.toLowerCase()
      );
      const isInDynamicValues = Object.keys(dynamicValues).find(
        (field) => field.toLowerCase() === columnName.toLowerCase()
      );
      if (field) {
        return `this.modifiedRecord['${field.DatabaseColumnName}']`;
      } else if (isInDynamicValues) {
        return `this.dynamicValues['${isInDynamicValues}']`;
      }
      return _match;
    });
    rule.isDynamicValue = true;
  } else {
    rule.value = escapeQuotesAndSlash(rule.value);
  }

  switch (rule.operator) {
    case 'IS':
    case 'IS NOT':
      return isChecks(storageType, field, rule, objectField);
    case 'IS GREATER THAN':
    case 'IS SMALLER THAN':
    case 'IS GREATER THAN OR EQ':
    case 'IS SMALLER THAN OR EQ':
      // in case of null or undefined consider record value as 0
      return compareValues(field, rule, objectField && objectField.FieldType);
    case 'IS STEP COMPLETED':
    case 'IS STEP NOT COMPLETED':
      return compareBulkImportSteps(field, rule);
    case 'BETWEEN':
    case 'NOT BETWEEN':
      return prepareDateRange(field, rule);
    case 'IS NULL':
    case 'IS NOT NULL':
      return nullChecks(storageType, field, rule);
    case 'IS CLICKED':
    case 'IS CHECKED':
    case 'IS NOT CHECKED':
      return `${field} ${operatorMap[rule.operator]}`;
    case 'DOES’T HAVE ALL':
    case 'DOES’T HAVE ANY':
    case 'HAS ALL':
    case 'HAS ANY':
      return `this.advanceConditions(${field}, ${_surroundWithQuotes(rule.value, rule.isDynamicValue)}, "${
        rule.operator
      }", "${objectField?.FieldType}", "${objectField?.Settings?.fieldOfReference}")`;
    case 'IS VALID':
      return `(${field}, this.isCustomFieldValidationValid('${rule.key}'))`;
    case 'IS INVALID':
      return `(${field}, !this.isCustomFieldValidationValid('${rule.key}'))`;
    case 'DOES NOT CONTAIN':
      return `!!(${field} && !${field}.includes(${_surroundWithQuotes(rule.value, rule.isDynamicValue)}))`;
    case 'CONTAINS':
      return `!!${field} && ${field}.includes(${_surroundWithQuotes(rule.value, rule.isDynamicValue)})`;
    case 'IS LOGGED IN USER':
      return storageType === 'Array'
        ? `!!${field} && (${field}?.map(r => r + '').includes(this.loggedInUser+'') || (${checkRoles} && this.checkForRoles(${field})))`
        : `${field} == this.loggedInUser || (${checkRoles} && this.checkForRoles(${field}))`;
    case 'IS NOT LOGGED IN USER':
      return storageType === 'Array'
        ? `!!(${field} && (!${field}.includes(this.loggedInUser)) && (${checkRoles} && !this.checkForRoles(${field})))`
        : `${field} !== this.loggedInUser && (${checkRoles} && !this.checkForRoles(${field}))`;
    case 'formSaved':
      return `this.elementValues['saved'] === true`;
    case 'formCompleted':
      return `this.elementValues['submitted'] === true`;
    default:
      return 'false';
  }
};

// eslint-disable-next-line max-params
export const advanceConditions = (fieldValue, rule, condition, fieldType, fieldOfReference) => {
  let selectedItems = fieldValue || {};

  if (typeof selectedItems === 'object' && !Array.isArray(selectedItems)) {
    selectedItems = Object.keys(selectedItems);
    selectedItems = selectedItems.filter((id) => fieldValue[id]);
  } else if (typeof selectedItems === 'string') {
    selectedItems = selectedItems.split(',');
  }

  if (fieldType === 'USER' && fieldOfReference === 'Users') {
    selectedItems = selectedItems.map(String);
  }

  let itemsToCheck = rule ? rule.split(',') : [];

  switch (condition) {
    case 'IS NULL':
      return selectedItems.length === 0;
    case 'IS NOT NULL':
      return selectedItems.length !== 0;
    case 'IS':
      if (fieldType === 'CHECKBOX') {
        return selectedItems.includes(itemsToCheck[0]);
      }
      return selectedItems.length === 1 && selectedItems.includes(itemsToCheck[0]);
    case 'IS NOT':
      if (fieldType === 'CHECKBOX') {
        return selectedItems.length > 0 && !selectedItems.includes(itemsToCheck[0]);
      }
      return selectedItems.length !== 1 || !selectedItems.includes(itemsToCheck[0]);
    case 'DOES’T HAVE ALL':
      return difference(itemsToCheck, selectedItems).length > 0;
    case 'DOES’T HAVE ANY':
      return intersection(selectedItems, itemsToCheck).length === 0;
    case 'HAS ANY':
      return intersection(selectedItems, itemsToCheck).length > 0;
    default:
      //HAS ALL
      return difference(itemsToCheck, selectedItems).length === 0;
  }
};

/**
 * returns the result for an action based on action map and clusterResults.
 * i.e
 * actionMapping = {DISABLE: {0:true,1:false}} (cluster 0 has disable action while cluster 1 has enable action)
 * clusterResults = {0:false, 1:true} (there are two clusters and both cluster's condition results into false and true respectively.)
 * returned value will be last cluster's action that is index 1's action which is false.(so element will be enabled)
 * @param {string} action can be DISABLE/HIDE etc.
 * @param {Object} actionMapping should have map of actions based on different keys like All,R_1,Element-1 etc.
 * @param {Object} clusterResults should have results of all cluster conditions
 * @param {boolean} defaultResult set default result for action
 */
export const priorityBasedClusterResult = (action, actionMapping, clusterResults, defaultResult) => {
  if (actionMapping && actionMapping[action]) {
    let reversedKeys = Object.keys(actionMapping[action]).reverse();
    for (let index = 0; index < reversedKeys.length; index++) {
      if (clusterResults[reversedKeys[index]]) {
        return actionMapping[action][reversedKeys[index]];
      }
      //does this if no condition satisfied for disabled/show case
      if (['DISABLE', 'SHOW'].includes(action) && index === reversedKeys.length - 1) {
        return !actionMapping[action][reversedKeys[index]];
      }
    }
  }
  return defaultResult;
};

/**
 * returns the cluster index for an action based on action map and clusterResults.
 * i.e
 * actionMapping = {0:'action-a-configurations',1:'action-b-configurations'} (cluster 0 has disable action while cluster 1 has enable action)
 * clusterResults = {0:false, 1:true} (there are two clusters and both cluster's condition results into false and true respectively.)
 * returned value will be last positive cluster's index(1 in above case).
 * @param {Object} actionMapping should have map of actions based on different keys like All,R_1,Element-1 etc.
 * @param {Object} clusterResults should have results of all cluster conditions
 */
export const priorityBasedClusterIndex = (actionMapping, clusterResults) => {
  if (actionMapping) {
    let reversedKeys = Object.keys(actionMapping).reverse();
    for (let index = 0; index < reversedKeys.length; index++) {
      if (clusterResults[reversedKeys[index]]) {
        return reversedKeys[index];
      }
    }
  }
  return undefined;
};

/**
 * returns the template actions map for specific action based on priority.
 * i.e. (lowest priority) = All -> Row -> Division -> Column -> Element (Highest Priority)
 *
 * @param {string} action can be DISABLE/HIDE etc.
 * @param {Object} element should include element with ID
 * @param {Object} parent should include row, division and column ID
 * @param {Object} actionMapping should have map of actions based on different keys like All,R_1,Element-1 etc.
 */
export const priorityBasedActionMapping = (action, element, parent, actionMapping) => ({
  ...(actionMapping['All'] && actionMapping['All'][action]),
  ...(actionMapping[parent.rowId] && actionMapping[parent.rowId][action]),
  ...(actionMapping[parent.divisionId] && actionMapping[parent.divisionId][action]),
  ...(actionMapping[parent.columnId] && actionMapping[parent.columnId][action]),
  ...(actionMapping[element.ID] && actionMapping[element.ID][action])
});

/**
 * Returns the value which needs to be set based on config provided
 *
 * @param {Object} field has field configuration for which set value is called
 * @param {Object} valueConfig has set value configurations
 * @param {Object} dynamicValues has current record values and data loader values
 * @param {number} loggedInUser has loggedInUser's ID
 * @param {Object} dataLoaderValues has the business rule data loader's values
 */
export const setValueResults = (field, valueConfig, dynamicValues, loggedInUser) => {
  switch (field.FieldType) {
    case 'CHECKBOX':
      return getCheckBoxValue(field, valueConfig, dynamicValues[field.DatabaseColumnName]);
    case 'TAG':
      return getTagFieldValue(field, valueConfig, dynamicValues[field.DatabaseColumnName]);
    case 'DATE':
      return getDateValue(field, valueConfig, dynamicValues);
    case 'REFERENCE':
    case 'USER':
      return getReferenceValues(field, valueConfig, loggedInUser);
    case 'CURRENCY':
      return {
        currency: dynamicValues[`${field.DatabaseColumnName}Currency`],
        value: getSingleSelectValue(valueConfig, true, dynamicValues, loggedInUser)
      };
    case 'NUMBER':
    case 'SLIDER':
    case 'STAR_RATING':
    case 'LIKERT':
      return getSingleSelectValue(valueConfig, true, dynamicValues, loggedInUser);
    default:
      return getSingleSelectValue(valueConfig, false, dynamicValues, loggedInUser);
  }
};

/**
 * @param {Object} valueConfig has the value to set
 * @param {Object} field has field configuration
 * @param {number} loggedInUser has logged in user's id
 */
const getReferenceValues = (field, valueConfig, loggedInUser) => {
  const isString = field.FieldType === 'USER' && field.Settings.fieldOfReference === 'UserAndRoles';
  if (field.Settings.IsMultiSelect) {
    return getMultiSelectValues(valueConfig, loggedInUser, isString);
  } else if (valueConfig.setValueTo === 'null') {
    return null;
  } else if (valueConfig.setValueTo === '[[AuthUserId]]') {
    return isString ? '' + loggedInUser : loggedInUser;
  }
  return valueConfig.setValueTo;
};

/**
 * Returns list response based on available dynamic values
 *
 * @param {Object} valueConfig has the value to set
 * @param {Object} dynamicValues has record value
 * @param {number} loggedInUser has logged in user's id
 */
const getMultiSelectValues = (valueConfig, loggedInUser, isString) => {
  if (valueConfig.setValueTo.includes('null')) {
    return [];
  }
  let valueToReturn = [...valueConfig.setValueTo];
  if (valueToReturn.includes('[[AuthUserId]]')) {
    valueToReturn[valueToReturn.indexOf('[[AuthUserId]]')] = isString ? '' + loggedInUser : loggedInUser;
  }
  return valueToReturn;
};

/**
 * Returns response based on available dynamic values
 *
 * @param {Object} valueConfig has the value to set
 * @param {Object} dynamicValues has record value
 * @param {number} loggedInUser has logged in user's id
 */
const getSingleSelectValue = (valueConfig, isNumber, dynamicValues, loggedInUser) => {
  if (valueConfig.setValueTo === 'null') return null;

  let result =
    typeof valueConfig.setValueTo === 'string'
      ? valueConfig.setValueTo
          .replace(/\[\[AuthUserId\]\]/i, () => `${loggedInUser || ''}`)
          .replace(/\[\[([^\]]+)\]\]/g, (_match, columnName) => dynamicValues[columnName] || '')
      : valueConfig.setValueTo;

  return isNumber ? Number(result) : result;
};

/**
 * Returns the tag field's value to be set. based on given configurations.
 *
 * 'null' with option 'all' will result into {}
 * 'null' with option ['A'] will result into setting A to false
 * ['all'] will result into all suggestions selected
 * ['A','B'] will result into current values + A & B selected
 *
 * @param {Object} field has the suggestions for field
 * @param {Object} valueConfig has the value to set
 * @param {Object} currentValue has the current field's value in record
 */
const getCheckBoxValue = (field, valueConfig, currentValue) => {
  let result;
  if (valueConfig.setValueTo === 'null') {
    //for clear all
    if (valueConfig.option.includes('all')) {
      return {};
    }
    //for clear specific
    result = valueConfig.option.reduce((result, option) => {
      result[option] = false;
      return result;
    }, {});
  } else if (valueConfig.setValueTo.includes('all')) {
    //for selecting all
    result = field.Suggestions.reduce((result, suggestion) => {
      result[suggestion.databaseValue] = true;
      return result;
    }, {});
  } else {
    //for selecting specific
    result = valueConfig.setValueTo.reduce((result, option) => {
      result[option] = true;
      return result;
    }, {});
  }

  return { ...currentValue, ...result };
};

/**
 * Returns the tag field's value to be set. based on given configurations.
 *
 * ['null'] will result into []
 * ['all'] will result into uniq of current value and all available suggestions.
 * ['A','B'] will result into 'A','B'
 *
 * @param {Object} field has the suggestions for field
 * @param {Object} valueConfig has the value to set
 * @param {Object} currentValue has the current field's value in record
 */
const getTagFieldValue = (field, valueConfig, currentValue) => {
  if (valueConfig.setValueTo.includes('null')) {
    //for clear all
    return [];
  } else if (valueConfig.setValueTo.includes('all')) {
    //for selecting all
    return uniq([...currentValue, ...field.Suggestions]);
  }
  //for selecting specific
  return [...valueConfig.setValueTo];
};

/**
 * Returns the date field's value to be set. based on given configurations.
 *
 * 'null'/'clear' etc. will result into null
 * 'new Date()' will result into current Date.
 * '[[column1]]' will result into column value from current record or data loader values
 * '[[column]] + 1d' will result into column value +/- 1 day.
 *  other abbreviations are years : y, quarters : Q, months : M, weeks : w, hours : h, minutes : m, seconds : s, milliseconds : ms
 *
 * @param {Object} valueConfig has the value to set
 * @param {Object} dynamicValues has current record's values and data loader values
 * @param {Object} dataLoaderValues has the business rule data loader's values
 */
const getDateValue = (field, valueConfig, dynamicValues) => {
  if (
    !valueConfig.setValueTo ||
    valueConfig.setValueTo.toString().toLowerCase() === 'null' ||
    valueConfig.setValueTo.toString().toLowerCase() === 'clear'
  ) {
    return null;
  }
  //This regex will match any dynamic string format with optional date time parameters(eg: +5d)
  //below regex returns groups => ['Full String','group1','group2','group3','group4','+/- sign','integer number after +/-', 'd|y|Q|M|w|h|m|s|ms']
  //group 1 contains dynamic date field ie. contains group 2 or group 3
  //group 2 contains the date if it is from another field (eg: [[date3]])
  //group 3 contains the date if it is of the form 'new Date()'
  //group 4 contains the remaining part of dynamic date
  const dynamicDate = valueConfig.setValueTo.match(
    /((\[\[.+\]\])|(new Date\(\)))\s*((-|\+)\s*(\d+)(d|y|Q|M|w|h|ms|m|s))?/
  );
  let dateToReturn = null;
  if (!dynamicDate) {
    // For static dates
    let dateValue = valueConfig.setValueTo.trim();
    const dateFormatArray = ['YYYY-MM-DD', 'YYYY-MM-DD[T]HH:mm', moment.defaultFormat];
    for (const dtFormat of dateFormatArray) {
      if (moment(dateValue, dtFormat, true).isValid()) {
        dateToReturn = moment(dateValue, dtFormat);
        break;
      }
    }
    return getISOString(dateToReturn, field);
  }
  //Checking for the optional parameters validity eg([[fgd]]day, expected [[fgd]] + 5d)
  const newMatcher = dynamicDate[1];
  const optionalEditDateParams = dynamicDate[4];
  const plusMinusSign = dynamicDate[5];
  const editFrequency = dynamicDate[6];
  const editLiteral = dynamicDate[7];
  if (!optionalEditDateParams && valueConfig.setValueTo.trim().length > newMatcher.length) {
    return null;
  }
  // For dynamic dates
  let dateValue = dynamicDate[1].trim();
  dateValue = dateValue
    .replace(/new Date\(\)/i, () => moment().format(moment.defaultFormat))
    .replace(/\[\[([^\]]+)\]\]/, (_match, columnName) => dynamicValues[columnName]);
  if (
    moment(dateValue, moment.defaultFormat)
      .add(editFrequency * (plusMinusSign === '-' ? -1 : 1, editLiteral))
      .isValid()
  ) {
    dateToReturn = moment(dateValue, moment.defaultFormat).add(
      editFrequency * (plusMinusSign === '-' ? -1 : 1),
      editLiteral
    );
  }
  return getISOString(dateToReturn, field);
};

/**
 * Returns date iso string modified based on config
 *
 * @param {Date} date date to be modified
 * @param {object} field field holding settings for time
 */
const getISOString = (date, field) => {
  if (!date) return null;
  date.set({
    hour: field.Settings.IsTimeInputEnable === 1 ? date.get('hour') : 0,
    minute: field.Settings.IsTimeInputEnable === 1 ? date.get('minute') : 0,
    second: 0,
    millisecond: 0
  });
  return date.toISOString(true);
};

const _surroundWithQuotes = (value, isDynamic) => (isDynamic ? value : `'${value}'`);
